/**
 * 音频文件MD5计算工具
 * 专门用于计算下载的音频文件的MD5值，作为缓存唯一标识
 */

import SparkMD5 from 'spark-md5';

/**
 * 计算Blob对象的MD5值
 * @param {Blob} blob - 音频文件的Blob对象
 * @param {Function} onProgress - 进度回调函数，参数为进度百分比(0-100)
 * @returns {Promise<string>} MD5值
 */
export function calculateBlobMD5(blob, onProgress = null) {
  return new Promise((resolve, reject) => {
    const chunkSize = 2 * 1024 * 1024; // 2MB chunks
    const chunks = Math.ceil(blob.size / chunkSize);
    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    
    console.log(`🔢 [音频MD5] 开始计算音频文件MD5，大小: ${(blob.size / 1024 / 1024).toFixed(2)}MB，分成 ${chunks} 块`);
    
    fileReader.onload = (e) => {
      try {
        spark.append(e.target.result);
        currentChunk++;
        
        // 计算进度
        const progress = Math.round((currentChunk / chunks) * 100);
        console.log(`🔢 [音频MD5] 计算进度: ${progress}% (${currentChunk}/${chunks})`);
        
        // 调用进度回调
        if (onProgress && typeof onProgress === 'function') {
          onProgress(progress);
        }
        
        if (currentChunk < chunks) {
          loadNext();
        } else {
          const md5 = spark.end();
          console.log(`🔢 [音频MD5] MD5计算完成: ${md5}`);
          resolve(md5);
        }
      } catch (error) {
        console.error('🔢 [音频MD5] MD5计算过程中出错:', error);
        reject(error);
      }
    };
    
    fileReader.onerror = (error) => {
      console.error('🔢 [音频MD5] 文件读取失败:', error);
      reject(new Error('文件读取失败'));
    };
    
    function loadNext() {
      try {
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, blob.size);
        const chunk = blob.slice(start, end);
        fileReader.readAsArrayBuffer(chunk);
      } catch (error) {
        console.error('🔢 [音频MD5] 读取文件块失败:', error);
        reject(error);
      }
    }
    
    // 开始读取第一块
    loadNext();
  });
}

/**
 * 快速计算音频文件的部分MD5（用于快速缓存键生成）
 * 只计算文件的开头、中间和结尾部分，速度更快但唯一性稍低
 * @param {Blob} blob - 音频文件的Blob对象
 * @returns {Promise<string>} 部分MD5值
 */
export function calculateFastMD5(blob) {
  return new Promise((resolve, reject) => {
    const sampleSize = 64 * 1024; // 64KB samples
    const spark = new SparkMD5.ArrayBuffer();
    let samplesRead = 0;
    const totalSamples = 3; // 开头、中间、结尾
    
    console.log(`⚡ [音频快速MD5] 开始快速MD5计算，文件大小: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    
    // 定义采样位置
    const samplePositions = [
      { start: 0, end: Math.min(sampleSize, blob.size) }, // 开头
      { start: Math.max(0, Math.floor(blob.size / 2) - sampleSize / 2), end: Math.min(blob.size, Math.floor(blob.size / 2) + sampleSize / 2) }, // 中间
      { start: Math.max(0, blob.size - sampleSize), end: blob.size } // 结尾
    ];
    
    function readNextSample() {
      if (samplesRead >= totalSamples) {
        const md5 = spark.end();
        console.log(`⚡ [音频快速MD5] 快速MD5计算完成: ${md5}`);
        resolve(md5);
        return;
      }
      
      const position = samplePositions[samplesRead];
      const chunk = blob.slice(position.start, position.end);
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          spark.append(e.target.result);
          samplesRead++;
          console.log(`⚡ [音频快速MD5] 已读取样本 ${samplesRead}/${totalSamples}`);
          readNextSample();
        } catch (error) {
          console.error('⚡ [音频快速MD5] 处理样本失败:', error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        console.error('⚡ [音频快速MD5] 读取样本失败:', error);
        reject(error);
      };
      
      reader.readAsArrayBuffer(chunk);
    }
    
    readNextSample();
  });
}

/**
 * 生成基于用户和MD5的缓存键
 * @param {string} userId - 用户ID
 * @param {string} songId - 歌曲ID
 * @param {string} md5 - 完整或部分MD5值
 * @param {boolean} useShort - 是否使用MD5的后8位
 * @returns {string} 缓存键
 */
export function generateMD5CacheKey(userId, songId, md5, useShort = true) {
  if (!userId || !songId) {
    console.warn('🔑 [缓存键] 用户ID或歌曲ID缺失，使用基础格式');
    return `song_${songId}`;
  }

  if (!md5) {
    console.warn('🔑 [缓存键] MD5值缺失，使用用户+歌曲ID格式');
    return `user_${userId}_song_${songId}`;
  }
  
  const md5Suffix = useShort && md5.length >= 8 ? md5.slice(-8) : md5;
  const cacheKey = `user_${userId}_song_${songId}_${md5Suffix}`;
  
  console.log(`🔑 [缓存键] 生成用户缓存键: ${cacheKey} (用户: ${userId}, 原MD5: ${md5})`);
  return cacheKey;
}

/**
 * 从缓存键中提取用户ID
 * @param {string} cacheKey - 缓存键
 * @returns {string|null} 用户ID
 */
export function extractUserIdFromCacheKey(cacheKey) {
  try {
    const match = cacheKey.match(/^user_(\d+)_song_/);
    return match ? match[1] : null;
  } catch (error) {
    console.error('🔑 [缓存键] 提取用户ID失败:', error);
    return null;
  }
}

/**
 * 从缓存键中提取歌曲ID
 * @param {string} cacheKey - 缓存键
 * @returns {string|null} 歌曲ID
 */
export function extractSongIdFromCacheKey(cacheKey) {
  try {
    // 支持新格式: user_{userId}_song_{songId}_{md5}
    let match = cacheKey.match(/^user_\d+_song_(\d+)_/);
    if (match) return match[1];
    
    // 支持旧格式: song_{songId}_{md5} 或 song_{songId}
    match = cacheKey.match(/^song_(\d+)(_|$)/);
    if (match) return match[1];
    
    return null;
  } catch (error) {
    console.error('🔑 [缓存键] 提取歌曲ID失败:', error);
    return null;
  }
}

/**
 * 检查缓存键是否属于指定用户
 * @param {string} cacheKey - 缓存键
 * @param {string} userId - 用户ID
 * @returns {boolean} 是否属于该用户
 */
export function isCacheKeyBelongsToUser(cacheKey, userId) {
  const keyUserId = extractUserIdFromCacheKey(cacheKey);
  if (keyUserId) {
    return keyUserId === String(userId);
  }
  
  // 旧格式缓存键（没有用户信息）被认为是"无主"的，需要进一步验证
  return false;
}

/**
 * 从音频URL提取歌曲ID
 * @param {string} url - 音频URL
 * @returns {string|null} 歌曲ID
 */
export function extractSongIdFromUrl(url) {
  try {
    // 匹配 /api/music/{id}/stream 或 /api/music/{id}/video 格式
    const match = url.match(/\/api\/music\/(\d+)\/(?:stream|video)/);
    return match ? match[1] : null;
  } catch (error) {
    console.error('🔑 [缓存键] 提取歌曲ID失败:', error);
    return null;
  }
}
